import type {
  AcademicRecord,
  AccessPolicy,
  StudentStatus,
  TrustRecord,
  VerificationLevel,
  VerificationMethod,
  VerificationState,
} from '../types/verification'

/** Ordered weakest to strongest. Comparison is by index, never by string. */
export const LEVEL_ORDER: VerificationLevel[] = [
  'unverified',
  'email_verified',
  'document_verified',
  'institution_verified',
  'institution_integrated',
]

export function levelRank(level: VerificationLevel): number {
  return LEVEL_ORDER.indexOf(level)
}

export function meetsLevel(held: VerificationLevel, required: VerificationLevel): boolean {
  return levelRank(held) >= levelRank(required)
}

/**
 * The label a level is allowed to display, and — just as importantly — what it
 * must NOT claim. Email verification proves access to an address; it does not
 * prove current enrolment, so it can never render as "Verified student".
 */
export const LEVEL_LABEL: Record<VerificationLevel, string> = {
  unverified: 'Academic information not verified',
  email_verified: 'Institutional email verified',
  document_verified: 'Academic identity verified',
  institution_verified: 'Verified student',
  institution_integrated: 'Verified student',
}

export const LEVEL_MEANING: Record<VerificationLevel, string> = {
  unverified: 'Everything here was entered by this person and has not been checked by anyone.',
  email_verified:
    'Access to an approved institutional email address was confirmed. This does not confirm current enrolment, programme or level.',
  document_verified:
    'Academic documentation was reviewed and accepted by the Campuzon verification team. This is not an institutional confirmation.',
  institution_verified:
    'An authorised representative of the institution confirmed this affiliation directly.',
  institution_integrated:
    'Confirmed against the institution’s own record through an authorised institutional connection.',
}

export const LEVEL_DOES_NOT_ASSERT: Record<VerificationLevel, string> = {
  unverified: 'It asserts nothing at all.',
  email_verified: 'It does not assert that this person is currently enrolled.',
  document_verified: 'It does not assert that the institution itself confirmed anything.',
  institution_verified: 'It does not assert anything about conduct, safety or transactions.',
  institution_integrated: 'It does not assert anything about conduct, safety or transactions.',
}

export const STATUS_LABEL: Record<StudentStatus, string> = {
  applicant: 'Applicant',
  admitted: 'Admitted',
  enrolled: 'Enrolled',
  active_student: 'Active student',
  leave_of_absence: 'On leave of absence',
  graduating: 'Graduating',
  graduated: 'Graduated',
  withdrawn: 'Withdrawn',
  suspended: 'Suspended',
  expelled: 'Expelled',
  alumni: 'Alumni',
  unknown: 'Not established',
}

/**
 * Statuses that must never be shown outside the person and their institution.
 * A disciplinary state is not a public trust signal.
 */
const SENSITIVE_STATUSES: StudentStatus[] = ['suspended', 'expelled', 'withdrawn', 'leave_of_absence']

export function isSensitiveStatus(status: StudentStatus): boolean {
  return SENSITIVE_STATUSES.includes(status)
}

export const METHOD_LABEL: Record<VerificationMethod, string> = {
  institutional_email: 'Institutional email',
  document: 'Academic document',
  institution_review: 'Institution review',
  integration: 'Institutional connection',
}

export const STATE_LABEL: Record<VerificationState, string> = {
  declared: 'Declared, not verified',
  pending_verification: 'Verification requested',
  under_review: 'Under review',
  verified: 'Verified',
  rejected: 'Not verified',
  expired: 'Verification expired',
  suspended: 'Verification suspended',
  revoked: 'Verification revoked',
  requires_reverification: 'Reverification needed',
}

/** Only these states may render a badge at all. */
export function badgeVisible(state: VerificationState): boolean {
  return state === 'verified'
}

/** Days until expiry, or null when the record has no expiry set. */
export function daysUntil(iso: string | null, now = new Date()): number | null {
  if (!iso) return null
  const diff = new Date(iso).getTime() - now.getTime()
  return Math.ceil(diff / 86_400_000)
}

export function expiryTone(iso: string | null, now = new Date()): 'ok' | 'warn' | 'danger' | null {
  const days = daysUntil(iso, now)
  if (days === null) return null
  if (days <= 0) return 'danger'
  if (days <= 30) return 'warn'
  return 'ok'
}

/**
 * Derives the trust record other domains consume.
 *
 * Deliberately derived, never stored: an expired verification cannot keep
 * reporting `currentStudent`, and an admitted applicant cannot acquire student
 * standing just because an admissions record exists.
 */
export function trustFrom(record: AcademicRecord | null, now = new Date()): TrustRecord {
  const empty: TrustRecord = {
    academicIdentityVerified: false,
    institutionVerified: false,
    currentStudent: false,
    isAlumni: false,
    level: 'unverified',
    institutionId: null,
    campusId: null,
    programme: null,
    expiresAt: null,
  }
  if (!record) return empty

  const live =
    record.verification.state === 'verified' &&
    (record.verification.expiresAt === null ||
      new Date(record.verification.expiresAt).getTime() > now.getTime())

  const level = live ? record.verification.level : 'unverified'

  return {
    academicIdentityVerified: live && levelRank(level) >= levelRank('document_verified'),
    institutionVerified: live && levelRank(level) >= levelRank('institution_verified'),
    // Current enrolment is a claim only an institutional source can support.
    currentStudent:
      live &&
      levelRank(level) >= levelRank('institution_verified') &&
      (record.status === 'active_student' || record.status === 'enrolled' || record.status === 'graduating'),
    isAlumni: record.status === 'alumni' || record.status === 'graduated',
    level,
    institutionId: record.institutionId,
    campusId: record.campusId,
    programme: record.programme.value,
    expiresAt: record.verification.expiresAt,
  }
}

export interface PolicyDecision {
  allowed: boolean
  /** Why, in the words the person reads. */
  message: string
  /** What they can do about it. */
  remedy: string | null
}

/**
 * Evaluates a configured policy against a trust record. Components ask this;
 * they never inspect verification fields themselves, and a client-side result
 * is presentation only — the services evaluate the same policy authoritatively.
 */
export function evaluatePolicy(
  policy: AccessPolicy,
  trust: TrustRecord,
  status: StudentStatus | null,
): PolicyDecision {
  if (!meetsLevel(trust.level, policy.requiredLevel)) {
    return {
      allowed: false,
      message: `This needs ${LEVEL_LABEL[policy.requiredLevel].toLowerCase()}. Yours is currently ${LEVEL_LABEL[
        trust.level
      ].toLowerCase()}.`,
      remedy: policy.fallback,
    }
  }
  if (policy.requiredInstitutionId && trust.institutionId !== policy.requiredInstitutionId) {
    return {
      allowed: false,
      message: 'This is restricted to a different institution.',
      remedy: policy.fallback,
    }
  }
  if (policy.requiredStatuses && (status === null || !policy.requiredStatuses.includes(status))) {
    return {
      allowed: false,
      message: `This needs one of: ${policy.requiredStatuses
        .map((entry) => STATUS_LABEL[entry].toLowerCase())
        .join(', ')}.`,
      remedy: policy.fallback,
    }
  }
  return { allowed: true, message: 'You meet the requirement for this.', remedy: null }
}

/** Masks an institutional address for display. Never renders it in full. */
export function maskEmail(email: string): string {
  const [local, domain] = email.split('@')
  if (!domain) return '•••'
  const head = local.slice(0, 1)
  return `${head}${'•'.repeat(Math.max(local.length - 1, 3))}@${domain}`
}

/** Masks an institutional identifier when it must be referenced at all. */
export function maskIdentifier(identifier: string | null): string {
  if (!identifier) return 'Not held'
  return `${'•'.repeat(Math.max(identifier.length - 3, 3))}${identifier.slice(-3)}`
}

export function addMonths(iso: string, months: number): string {
  const date = new Date(iso)
  date.setMonth(date.getMonth() + months)
  return date.toISOString()
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

/**
 * The lifecycle transition a status change implies. Graduation never deletes
 * the record — it moves it, keeping the verified relationship intact.
 */
export function nextLifecycle(status: StudentStatus): StudentStatus | null {
  if (status === 'applicant') return 'admitted'
  if (status === 'admitted') return 'enrolled'
  if (status === 'enrolled') return 'active_student'
  if (status === 'active_student') return 'graduating'
  if (status === 'graduating') return 'graduated'
  if (status === 'graduated') return 'alumni'
  return null
}
